import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import dayjs from 'lib/dayjs';
import { toast } from 'react-toastify';
import {
  useGetLogUpdateRequestsQuery,
  useApproveLogUpdateBulkMutation,
} from 'redux/features/timeLogApi';

import TableSkeleton from 'components/TableSkeleton';
import MonthYearNavigation from 'components/ui/MonthYearNavigation';
import Icon from '../../components/AppIcon';
import CommandPalette from '../../components/ui/CommandPalette';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import AdminLogUpdateListModal from './components/AdminLogUpdateListModal';

const AdminRequestManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Opened from the salary page: open this employee's requests and go back there after submit
  const linkedEmployeeId = searchParams.get('employeeId');
  const rawReturnTo = searchParams.get('returnTo');
  const returnTo =
    rawReturnTo?.startsWith('/') && !rawReturnTo.startsWith('//') ? rawReturnTo : null;
  const linkedOpened = useRef(false);

  const [selectedYear, setSelectedYear] = useState(
    Number(searchParams.get('year')) || new Date()?.getFullYear(),
  );
  const [selectedMonth, setSelectedMonth] = useState(
    Number(searchParams.get('month')) || new Date()?.getMonth() + 1,
  );
  const [selectedRequest, setSelectedRequest] = useState();
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedEmployeeRequest, setSelectedEmployeeRequest] = useState([]);

  const [isLogUpdateModalOpen, setIsLogUpdateModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
  });

  const {
    data: logUpdateData,
    refetch: refetchLogUpdates,
    isLoading: isLoadingLogUpdates,
  } = useGetLogUpdateRequestsQuery({
    year: selectedYear,
    month: selectedMonth,
  });

  const [approveLogUpdateBulk, { isLoading: isApprovingLogUpdate }] =
    useApproveLogUpdateBulkMutation();

  const applyFilters = useCallback(() => {
    let filtered = logUpdateData?.data || [];

    // Filter by search
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter((request) => {
        return (
          request?.employeeName?.toLowerCase()?.includes(searchTerm) ||
          request?.employeeId?.toLowerCase()?.includes(searchTerm) ||
          request?.comment?.toLowerCase()?.includes(searchTerm)
        );
      });
    }

    const map = new Map();

    filtered.forEach((item) => {
      const key = item.employeeId;
      const existing = map.get(key);
      map.set(key, {
        ...item,
        count: (existing?.count || 0) + 1,
        hasPending: existing?.hasPending || item.status === 'PENDING',
      });
    });

    filtered = [...map.values()];
    setFilteredRequests(filtered);
  }, [logUpdateData, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    if (isLogUpdateModalOpen && selectedRequest && logUpdateData?.data) {
      const res = logUpdateData?.data?.filter(
        (req) => req?.employeeId === selectedRequest?.employeeId,
      );
      setSelectedEmployeeRequest(res);
    }
  }, [logUpdateData, isLogUpdateModalOpen, selectedRequest]);

  useEffect(() => {
    if (!linkedEmployeeId || linkedOpened.current || !logUpdateData?.data) return;
    const mine = logUpdateData.data.filter((req) => req?.employeeId === linkedEmployeeId);
    if (!mine.length) return;
    linkedOpened.current = true;
    setSelectedRequest(mine[0]);
    setSelectedEmployeeRequest(mine);
    setIsLogUpdateModalOpen(true);
  }, [linkedEmployeeId, logUpdateData]);

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    // Pass all requests for this employee
    const res = logUpdateData?.data?.filter((req) => req?.employeeId === request?.employeeId);
    setSelectedEmployeeRequest(res);
    setIsLogUpdateModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      APPROVED: { color: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      REJECTED: { color: 'bg-red-100 text-red-800', icon: 'XCircle' },
      CANCELLED: { color: 'bg-gray-100 text-gray-800', icon: 'XCircle' },
    };

    const config = statusConfig?.[status] || statusConfig?.['PENDING'];

    return (
      <div
        className={`inline-flex items-center px-3 py-1 gap-2 rounded-full text-[10px] font-bold uppercase tracking-wider ${config?.color}`}
      >
        <Icon name={config?.icon} size={14} />
        {status}
      </div>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-secondary-50">
      <Header />
      <Sidebar />
      <CommandPalette />
      <AdminLogUpdateListModal
        isOpen={isLogUpdateModalOpen}
        onClose={() => setIsLogUpdateModalOpen(false)}
        onApprove={async (payload) => {
          try {
            const res = await approveLogUpdateBulk(payload);
            if (res.error) {
              return console.error(res.error.data?.message);
            }
            toast.success(`Requests processed successfully`);
            refetchLogUpdates();
            if (returnTo) navigate(returnTo);
          } catch (error) {
            console.log(error);
          }
        }}
        requests={selectedEmployeeRequest}
        isSubmitting={isApprovingLogUpdate}
      />
      {/* Main Content */}
      <div className="lg:ml-60 pt-16">
        <motion.div
          className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div className="mb-8" variants={itemVariants}>
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">Request Management</h1>
                <p className="text-text-secondary">
                  Review and manage employee log update requests
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
            variants={itemVariants}
          >
            <div className="w-full max-w-lg">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Icon name="ClipboardList" size={24} className="text-primary-500" />
                Employee Requests{' '}
                <span className="text-sm font-black bg-primary-100 text-primary-700 px-3 py-1 rounded-full border border-primary-200 shadow-sm ml-2">
                  EMPLOYEE {filteredRequests?.filter((req) => !req.hasPending)?.length || 0}/
                  {filteredRequests?.length || 0}
                </span>
              </h2>
            </div>
            <div className="bg-surface p-1 rounded-xl border border-border">
              <MonthYearNavigation
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                setSelectedMonth={setSelectedMonth}
                setSelectedYear={setSelectedYear}
              />
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div
            className="bg-surface rounded-2xl border border-border p-4 mb-6 shadow-sm"
            variants={itemVariants}
          >
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Filter */}
              <div className="flex-1">
                <div className="relative">
                  <Icon
                    name="Search"
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
                  />
                  <input
                    type="text"
                    placeholder="Search by Employee Name or ID..."
                    value={filters?.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e?.target?.value }))}
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary-500 transition-all"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Table */}
          <motion.div
            className="bg-surface rounded-2xl border border-border overflow-hidden shadow-sm"
            variants={itemVariants}
          >
            {isLoadingLogUpdates || filteredRequests?.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-auto h-[calc(100vh-450px)]">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-secondary-50/50 sticky top-0 z-10 backdrop-blur-sm">
                      <tr>
                        {/* Employee Column: Left aligned, plenty of padding */}
                        <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider w-full">
                          Employee
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider whitespace-nowrap">
                          Log Date
                        </th>

                        {/* Action Column: Right aligned to match the button */}
                        <th className="px-6 py-4 text-right text-xs font-bold pr-24 text-text-secondary uppercase tracking-wider whitespace-nowrap">
                          Action
                        </th>
                      </tr>
                    </thead>

                    {isLoadingLogUpdates ? (
                      <TableSkeleton rows={3} columns={6} />
                    ) : (
                      <tbody className="bg-surface divide-y divide-border">
                        {filteredRequests?.map((request) => {
                          const initials = request?.employeeName
                            ?.split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2);

                          return (
                            <tr
                              key={request?.id}
                              className="group transition-all duration-200 hover:bg-primary-50/50 border-b border-border last:border-0"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-4">
                                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                    {initials || 'U'}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-sm font-bold text-text-primary group-hover:text-primary transition-colors">
                                      {request?.employeeName || 'Unknown Employee'}
                                    </span>
                                    <span className="text-xs font-mono text-text-secondary mt-0.5">
                                      #{request?.employeeId}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-text-secondary">
                                  {request?.logDate || request?.date
                                    ? dayjs(request?.logDate || request?.date).format('DD MMM YYYY')
                                    : '--'}
                                </span>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <button
                                  onClick={() => handleViewRequest(request)}
                                  className={`inline-flex items-center gap-2 px-4 py-2 font-bold text-xs rounded-xl transition-all duration-200 shadow-sm active:scale-95 border ${
                                    request?.hasPending
                                      ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 border-primary-100 hover:border-primary-200'
                                      : 'bg-green-50 text-green-700 hover:bg-green-100 border-green-100 hover:border-green-200'
                                  }`}
                                >
                                  <span>View Details</span>
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-[10px] ${
                                      request?.hasPending
                                        ? 'bg-primary-200 text-primary-800'
                                        : 'bg-green-200 text-green-800'
                                    }`}
                                  >
                                    {request?.count || 0}
                                  </span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    )}
                  </table>
                </div>

                {/* Mobile Card List */}
                <div className="lg:hidden overflow-auto h-[calc(100vh-450px)]">
                  <div className="p-4 space-y-4">
                    {filteredRequests?.map((request) => {
                      return (
                        <div
                          key={request?.id}
                          className="bg-background border border-border rounded-lg p-4 space-y-3"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium text-text-primary">
                                {request?.employeeName || 'Unknown Employee'}
                              </h3>
                              <p className="text-sm text-text-secondary">{request?.employeeId}</p>
                            </div>
                            {request?.status !== 'PENDING' && getStatusBadge(request?.status)}
                          </div>

                          <div className="text-sm">
                            <span className="text-text-secondary">Log Date:</span>
                            <p className="text-text-primary font-medium">
                              {dayjs(request.logDate).format('DD MMM YYYY')}
                            </p>
                          </div>

                          <div className="text-sm">
                            <span className="text-text-secondary">Reason:</span>
                            <p className="text-text-primary">
                              {request?.comment || request?.reason || 'No reason provided'}
                            </p>
                            {request?.unplanned_leave_id && (
                              <span className="mt-1 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                Requested from unplanned leave
                              </span>
                            )}
                          </div>

                          <button
                            onClick={() => handleViewRequest(request)}
                            className={`px-4 py-2 font-bold text-xs rounded-xl transition-all duration-200 shadow-sm active:scale-95 border ${
                              request?.hasPending
                                ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 border-primary-100 hover:border-primary-200'
                                : 'bg-green-50 text-green-700 hover:bg-green-100 border-green-100 hover:border-green-200'
                            }`}
                          >
                            View Details
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-12">
                <Icon name="Calendar" size={48} className="mx-auto text-text-secondary mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">No request found</h3>
                <p className="text-text-secondary">
                  There are no requests at the moment for the selected month.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminRequestManagement;
